// UltraFlex Admin — Permissions & Role Management
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Shield, Plus, Edit3, Trash2, Save, X, Users, Check,
  ChevronDown, ChevronRight, AlertTriangle, UserCog, Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPermissions = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('roles');
  const [roles, setRoles] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', permissions: [] });
  const [expandedCats, setExpandedCats] = useState({});
  const [assignModal, setAssignModal] = useState(null); // staff object or null
  const [error, setError] = useState(null);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, staffRes, catalogRes] = await Promise.all([
        fetch(`${API_URL}/api/permissions/roles`, { headers }),
        fetch(`${API_URL}/api/permissions/staff`, { headers }),
        fetch(`${API_URL}/api/permissions/catalog`, { headers }),
      ]);
      setRoles((await rolesRes.json()).items || []);
      setStaffList((await staffRes.json()).items || []);
      setCatalog(await catalogRes.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // === Role CRUD ===
  const resetRoleForm = () => { setRoleForm({ name: '', description: '', permissions: [] }); setEditingRole(null); setError(null); };

  const startEditRole = (role) => {
    setRoleForm({ name: role.name, description: role.description || '', permissions: [...(role.permissions || [])] });
    setEditingRole(role.id); setShowRoleForm(true);
  };

  const togglePerm = (perm) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const toggleCategory = (catKey) => {
    const catPerms = catalog[catKey]?.permissions || [];
    const allChecked = catPerms.every(p => roleForm.permissions.includes(p));
    if (allChecked) {
      setRoleForm(prev => ({ ...prev, permissions: prev.permissions.filter(p => !catPerms.includes(p)) }));
    } else {
      setRoleForm(prev => ({ ...prev, permissions: [...new Set([...prev.permissions, ...catPerms])] }));
    }
  };

  const handleSaveRole = async (e) => {
    e.preventDefault(); setError(null);
    if (!roleForm.name) { setError('Role name is required'); return; }
    try {
      const url = editingRole ? `${API_URL}/api/permissions/roles/${editingRole}` : `${API_URL}/api/permissions/roles`;
      const method = editingRole ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(roleForm) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      fetchAll(); setShowRoleForm(false); resetRoleForm();
    } catch (err) { setError(err.message); }
  };

  const handleDeleteRole = async (id, name) => {
    if (!window.confirm(`Delete role "${name}"? Staff will lose this role.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/permissions/roles/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json()).detail || 'Cannot delete');
      fetchAll();
    } catch (err) { alert(err.message); }
  };

  // === Staff Role Assignment ===
  const handleAssignRole = async (staffId, roleId) => {
    await fetch(`${API_URL}/api/permissions/staff/${staffId}/roles`, {
      method: 'POST', headers, body: JSON.stringify({ role_id: roleId })
    });
    fetchAll(); setAssignModal(null);
  };

  const handleRemoveRole = async (staffId, roleId) => {
    await fetch(`${API_URL}/api/permissions/staff/${staffId}/roles/${roleId}`, { method: 'DELETE', headers });
    fetchAll();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-permissions-page">
      <div>
        <h1 className="text-2xl font-bold text-uf-text" data-testid="permissions-page-title">Permissions Management</h1>
        <p className="text-uf-text-muted text-sm mt-1">Define roles, assign permissions, and manage staff access.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-uf-surface rounded-lg p-1 border border-uf-border w-fit">
        {[{ id: 'roles', label: 'Roles', icon: Shield }, { id: 'staff', label: 'Staff Assignments', icon: Users }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`perm-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              tab === t.id ? 'bg-uf-red/15 text-uf-red' : 'text-uf-text-muted hover:text-uf-text'
            }`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {loading && <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto" /></div>}

      {/* === ROLES TAB === */}
      {!loading && tab === 'roles' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { resetRoleForm(); setShowRoleForm(!showRoleForm); }} data-testid="add-role-btn"
              className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm font-semibold">
              <Plus className="w-4 h-4" />{showRoleForm && !editingRole ? 'Cancel' : 'Create Role'}
            </button>
          </div>

          {showRoleForm && (
            <form onSubmit={handleSaveRole} className="bg-uf-surface rounded-xl border border-uf-border p-5 space-y-4" data-testid="role-form">
              <h3 className="text-sm font-semibold text-uf-text">{editingRole ? 'Edit Role' : 'Create New Role'}</h3>
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">{error}</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="text-uf-text-muted text-xs block mb-1">Role Name *</label>
                  <input value={roleForm.name} onChange={e => setRoleForm({ ...roleForm, name: e.target.value })}
                    className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="role-name-input" required /></div>
                <div><label className="text-uf-text-muted text-xs block mb-1">Description</label>
                  <input value={roleForm.description} onChange={e => setRoleForm({ ...roleForm, description: e.target.value })}
                    className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="role-desc-input" /></div>
              </div>
              {/* Permissions Grid */}
              <div>
                <label className="text-uf-text-muted text-xs block mb-2">Permissions ({roleForm.permissions.length} selected)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(catalog).map(([catKey, catInfo]) => {
                    const perms = catInfo.permissions || [];
                    const checkedCount = perms.filter(p => roleForm.permissions.includes(p)).length;
                    return (
                      <div key={catKey} className="bg-uf-surface-hi rounded-lg border border-uf-border p-3">
                        <button type="button" onClick={() => setExpandedCats(prev => ({ ...prev, [catKey]: !prev[catKey] }))}
                          className="w-full flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={checkedCount === perms.length && perms.length > 0}
                            onChange={() => toggleCategory(catKey)} className="rounded accent-uf-red" onClick={e => e.stopPropagation()} />
                          <span className="text-uf-text font-medium flex-1 text-left">{catInfo.label}</span>
                          <span className="text-uf-text-muted text-xs">{checkedCount}/{perms.length}</span>
                          <ChevronRight className={`w-3.5 h-3.5 text-uf-text-muted transition-transform ${expandedCats[catKey] ? 'rotate-90' : ''}`} />
                        </button>
                        {expandedCats[catKey] && (
                          <div className="mt-2 space-y-1 pl-6">
                            {perms.map(p => (
                              <label key={p} className="flex items-center gap-2 text-xs text-uf-text-muted cursor-pointer hover:text-uf-text">
                                <input type="checkbox" checked={roleForm.permissions.includes(p)}
                                  onChange={() => togglePerm(p)} className="rounded accent-uf-red" />
                                {p.split('.')[1]?.replace(/_/g, ' ')}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowRoleForm(false); resetRoleForm(); }} className="px-4 py-2 text-uf-text-muted text-sm">Cancel</button>
                <button type="submit" data-testid="submit-role-btn" className="flex items-center gap-2 bg-uf-red text-white px-5 py-2 rounded-lg text-sm font-semibold">
                  <Save className="w-4 h-4" />{editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          )}

          {/* Roles List */}
          <div className="space-y-3" data-testid="roles-list">
            {roles.map(role => (
              <div key={role.id} data-testid={`role-card-${role.id}`}
                className="bg-uf-surface rounded-xl border border-uf-border p-5 flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${role.is_system ? 'bg-blue-500/15' : 'bg-uf-red/15'}`}>
                  <Shield className={`w-5 h-5 ${role.is_system ? 'text-blue-400' : 'text-uf-red'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-uf-text font-semibold text-sm">{role.name}</h4>
                    {role.is_system && <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] rounded font-medium">SYSTEM</span>}
                  </div>
                  <p className="text-uf-text-muted text-xs mt-0.5">{role.description}</p>
                  <p className="text-uf-text-muted text-xs mt-1">{(role.permissions || []).length} permissions</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEditRole(role)} data-testid={`edit-role-${role.id}`}
                    className="p-2 text-uf-text-muted hover:text-uf-text rounded-lg hover:bg-uf-surface-hi"><Edit3 className="w-4 h-4" /></button>
                  {!role.is_system && (
                    <button onClick={() => handleDeleteRole(role.id, role.name)} data-testid={`delete-role-${role.id}`}
                      className="p-2 text-red-400/60 hover:text-red-400 rounded-lg hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === STAFF ASSIGNMENTS TAB === */}
      {!loading && tab === 'staff' && (
        <div className="space-y-3" data-testid="staff-assignments-list">
          {staffList.map(staff => (
            <div key={staff.id} data-testid={`staff-perm-${staff.id}`}
              className="bg-uf-surface rounded-xl border border-uf-border p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-uf-red/15 rounded-full flex items-center justify-center text-uf-red font-bold text-xs">
                {staff.first_name?.[0]}{staff.last_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-uf-text font-semibold text-sm">{staff.first_name} {staff.last_name}</h4>
                <p className="text-uf-text-muted text-xs">{staff.email} &middot; <span className="capitalize">{staff.role?.replace('_', ' ')}</span></p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(staff.roles || []).map(r => (
                    <span key={r.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-uf-red/10 text-uf-red rounded text-xs font-medium">
                      {r.name}
                      <button onClick={() => handleRemoveRole(staff.id, r.id)} className="hover:text-red-300" title="Remove role">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {(staff.roles || []).length === 0 && <span className="text-uf-text-muted text-xs italic">No roles assigned</span>}
                </div>
              </div>
              <button onClick={() => setAssignModal(staff)} data-testid={`assign-role-${staff.id}`}
                className="flex items-center gap-1 text-uf-text-muted hover:text-uf-text text-xs px-3 py-1.5 border border-uf-border rounded-lg hover:bg-uf-surface-hi">
                <Plus className="w-3.5 h-3.5" /> Assign Role
              </button>
            </div>
          ))}

          {/* Assign Role Modal */}
          {assignModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setAssignModal(null)}>
              <div className="bg-uf-surface rounded-xl border border-uf-border p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}
                data-testid="assign-role-modal">
                <h3 className="text-sm font-semibold text-uf-text mb-4">
                  Assign Role to {assignModal.first_name} {assignModal.last_name}
                </h3>
                <div className="space-y-2">
                  {roles.map(role => {
                    const assigned = (assignModal.roles || []).some(r => r.id === role.id);
                    return (
                      <button key={role.id} disabled={assigned}
                        onClick={() => handleAssignRole(assignModal.id, role.id)}
                        data-testid={`assign-${role.id}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left text-sm transition-all ${
                          assigned ? 'bg-uf-surface-hi text-uf-text-muted opacity-50' : 'bg-uf-surface-hi hover:bg-uf-red/10 text-uf-text'
                        }`}>
                        <Shield className="w-4 h-4" />
                        <div className="flex-1">
                          <p className="font-medium">{role.name}</p>
                          <p className="text-xs text-uf-text-muted">{(role.permissions || []).length} permissions</p>
                        </div>
                        {assigned && <Check className="w-4 h-4 text-emerald-400" />}
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => setAssignModal(null)}
                  className="w-full mt-4 text-uf-text-muted text-sm py-2 hover:text-uf-text">Close</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPermissions;
